import type { ReactNode } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useTranslation } from "react-i18next";

import { CbcConnectionTab } from "@/components/projects/CbcConnectionTab";
import { ConfigurationTenantForm } from "@/components/projects/ConfigurationTenantForm";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "@/components/ui/dialog";

// O mesmo do `DialogContent`, sem o X: o modal do gate só sai salvando ou voltando para a central.
const LOCKED_CONTENT_CLASS =
  "fixed left-[50%] top-[50%] z-50 grid max-h-[90vh] w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 overflow-y-auto border bg-background p-6 shadow-lg sm:rounded-lg";

interface ConnectionDialogProps {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Sem fechar por Esc, X ou clique fora (o gate da Execução); o padrão fecha. */
  locked?: boolean;
  /** No lugar do título padrão do diálogo. */
  title?: string;
  /** No lugar da descrição padrão do diálogo. */
  description?: string;
  /** Entre o cabeçalho e o formulário (o aviso de releitura do gate). */
  notice?: ReactNode;
  /** Depois do formulário (a saída do gate para a central). */
  footer?: ReactNode;
}

function ConnectionDialog({
  open,
  onOpenChange,
  locked = false,
  title,
  description,
  notice,
  footer,
  children,
}: Omit<ConnectionDialogProps, "projectId"> & { title: string; description: string; children: ReactNode }) {
  const body = (
    <>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
      </DialogHeader>
      {notice}
      {children}
      {footer}
    </>
  );
  if (!locked) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">{body}</DialogContent>
      </Dialog>
    );
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
          className={LOCKED_CONTENT_CLASS}
          onEscapeKeyDown={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
        >
          {body}
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}

/**
 * O modal da conexão do CBC (as credenciais com que o agente entra): o formulário da
 * aba Connections, dentro de um diálogo. Serve ao gate da Execução (`locked`) e ao botão
 * "Configure here" do selo do CBC. O formulário só monta com o diálogo aberto.
 */
export function CbcConnectionDialog({ projectId, title, description, ...rest }: ConnectionDialogProps) {
  const { t } = useTranslation();
  return (
    <ConnectionDialog
      {...rest}
      title={title ?? t("CBC connection")}
      description={description ?? t("Save the CBC credentials the agent signs in with.")}
    >
      <CbcConnectionTab projectId={projectId} onSaved={() => rest.onOpenChange(false)} />
    </ConnectionDialog>
  );
}

/**
 * O modal do tenant GROW Configuration (URL e client 100): o `ConfigurationTenantForm` com
 * `requireUrlAndClient`, que abre o client com 100. Mesmo uso do `CbcConnectionDialog`.
 */
export function GrowConnectionDialog({ projectId, title, description, ...rest }: ConnectionDialogProps) {
  const { t } = useTranslation();
  return (
    <ConnectionDialog
      {...rest}
      title={title ?? t("GROW connection")}
      description={description ?? t("Save the URL and client 100 of the GROW Configuration tenant.")}
    >
      <ConfigurationTenantForm projectId={projectId} requireUrlAndClient />
    </ConnectionDialog>
  );
}
